import { useState, Fragment } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import {
  Container,
  Section,
  SectionTitle,
  Button,
} from 'chaoskit/src/components'
import { graphql } from 'gatsby'
import { gradient, text } from 'chaoskit/src/assets/styles/utility'
import { MDXRenderer } from 'gatsby-plugin-mdx'
import { Helmet } from 'react-helmet'
import { theme as ckTheme } from 'chaoskit/src/assets/styles/theme'

import BaseLayout from '~layouts/BaseLayout'
import { HeroContent } from '~partials/Hero'
import Image from '~components/Image'
import { ReactComponent as FailoverConf2021LogoSvg } from '~media/failoverconf/2021/logo.svg'
import { ReactComponent as HeroShapes } from '~media/failoverconf/2021/shapes.svg'
import HelmetSEO from '~partials/global/HelmetSEO'
import Data from '~components/Data'
import { VideoModal } from '~components/Video'
import track from '~helpers/track'
import {
  HomeHero,
  HomeHeroGrid,
  HomeHeroGridContent,
  HomeHeroGridImage,
} from '~partials/home/Hero'
import { StylesHeaderVariables } from '~partials/header/UI'

const StylesFailoverConf2021 = {
  primary: '#43E9CF',
}

const VideoButton = ({ title, youtubeId, ...rest }) => {
  const theme = useTheme()
  const [videoModalOpen, setVideoModalOpen] = useState(false)

  return (
    <Fragment>
      <Button
        noContrast
        type="secondary"
        css={{
          background: StylesFailoverConf2021.primary,
          borderColor: StylesFailoverConf2021.primary,
          color: theme.fontColor.base,

          '&:hover, &:focus': {
            background: StylesFailoverConf2021.primary,
            borderColor: StylesFailoverConf2021.primary,
            color: theme.fontColor.base,
          },
        }}
        onClick={() => {
          track('Video recap watched', {
            label: `FailoverConf 2021: ${title}`,
          })

          setVideoModalOpen(true)
        }}
        {...rest}
      >
        View talk
      </Button>
      <VideoModal
        open={videoModalOpen}
        setIsOpen={setVideoModalOpen}
        title={title}
        src={`https://www.youtube.com/embed/${youtubeId}?modestbranding=1&rel=0&showinfo=0&autohide=1&autoplay=1&list=PLLIx5ktghjqKs6fPQmGfLsoKfiBU-ERGH`}
      />
    </Fragment>
  )
}

VideoButton.propTypes = {
  title: PropTypes.string.isRequired,
  youtubeId: PropTypes.string.isRequired,
}

const Talk = ({ data, index }) => {
  const theme = useTheme()

  return (
    <article
      css={{
        display: 'grid',
        gap: theme.space.large,

        [theme.mq.medium]: {
          gridTemplateColumns: index % 2 === 0 ? '1.5fr 2.5fr' : '2.5fr 1.5fr',
        },
      }}
    >
      <div
        css={{
          [theme.mq.medium]: {
            order: index % 2 === 0 ? 1 : null,
          },
        }}
      >
        <h3 css={{ marginBottom: theme.space.xsmall }}>{data.title}</h3>
        {data.byline && (
          <div
            css={{
              color: StylesFailoverConf2021.primary,
              fontWeight: theme.fontWeight.bold,

              a: [text.underline],
            }}
          >
            <Data>{data.byline}</Data>
          </div>
        )}
        <div css={{ marginTop: theme.space.medium }}>
          <Data>{data.blurb}</Data>
        </div>
        <div css={{ marginTop: theme.space.large }}>
          <VideoButton title={data.title} youtubeId={data.youtubeId} />
        </div>
      </div>
      <Image
        src={data.image}
        css={{
          alignSelf: 'end',

          [theme.mq.medium]: {
            position: 'sticky',
            bottom: 0,
          },
        }}
      />
    </article>
  )
}

Talk.propTypes = {
  data: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
}

// youtube URL pattern https://www.youtube.com/watch?v=[VIDEO ID HERE]&list=PLLIx5ktghjqItStdp_NUh3CQ_y4M49Gb1
const FailoverConf2021 = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()

  return (
    <BaseLayout
      headerProps={{
        hasDefaultOverlay: true,
      }}
    >
      <Helmet>
        <link rel="preconnect" href="https://use.typekit.net" />
        <link href="https://use.typekit.net/zhe7koh.css" rel="stylesheet" />
      </Helmet>
      <HelmetSEO
        title={pageData.title}
        description={pageData.description}
        image={pageData.socialMediaImage}
      />
      <HomeHero
        css={{
          [theme.mq.medium]: {
            paddingTop: StylesHeaderVariables(theme).height,
          },
        }}
      >
        <HeroContent css={{ ...gradient.dark(theme) }}>
          <HomeHeroGrid
            css={{
              [theme.mq.medium]: {
                paddingTop: 0,
              },
            }}
          >
            <HomeHeroGridImage
              css={{
                paddingLeft: theme.space.large,

                [theme.mq.small__max]: {
                  display: 'none',
                },
              }}
            >
              <HeroShapes />
            </HomeHeroGridImage>
            <HomeHeroGridContent>
              <FailoverConf2021LogoSvg />
            </HomeHeroGridContent>
          </HomeHeroGrid>
        </HeroContent>
      </HomeHero>
      <main
        css={{
          ...gradient.dark(theme),
          fontFamily: `interstate, ${ckTheme.fontFamily.base}`,

          [theme.mq.medium]: {
            marginTop: -theme.space.xlarge,
          },
        }}
        className="u-contrast"
      >
        <Container>
          <Section>
            <div
              css={{
                display: 'grid',
                gap: theme.space.large,

                [theme.mq.medium]: {
                  gridTemplateColumns: '1fr 3fr',
                },
              }}
            >
              <SectionTitle
                as="h2"
                title={pageData.introduction.heading}
                align={{ medium: 'left' }}
                css={{ marginBottom: '0 !important' }}
              />
              <div>
                <MDXRenderer>{pageData.introduction.content}</MDXRenderer>
              </div>
            </div>
          </Section>

          <Section>
            <div
              css={{
                display: 'grid',
                gap: theme.space.xlarge,

                [theme.mq.medium]: {
                  gap: theme.space.xlarge * 2,
                },
              }}
            >
              {pageData.talks.map((talk, index) => (
                <Talk key={talk.title} data={talk} index={index} />
              ))}
            </div>
          </Section>
        </Container>
      </main>
    </BaseLayout>
  )
}

FailoverConf2021.propTypes = {
  data: PropTypes.object.isRequired,
}

export default FailoverConf2021

export const pageQuery = graphql`
  query FailoverConf2021PageData {
    file(
      name: { eq: "failoverconf__2021" }
      absolutePath: { regex: "/src/data/" }
    ) {
      childMdx {
        frontmatter {
          title
          description
          socialMediaImage
          introduction {
            heading
            content
          }
          talks {
            title
            byline
            blurb
            youtubeId
            image
          }
        }
      }
    }
  }
`
